import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import useVacationLetterPDF from 'hooks/useVacationLetterPDF';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Badge, Button, Pagination, Spinner, Table } from 'react-bootstrap';

const VacationRequestsTable = ({
    perfil,
    onVacationClick,
    refreshTrigger = 0,
    formatDate
}) => {
    const [loading, setLoading] = useState(false);
    const [vacacionesSolicitadas, setVacacionesSolicitadas] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const { token } = useToken();
    const { generatePDF, isGenerating } = useVacationLetterPDF();
    const { showErrorToast, showSuccessToast } = useToast();
    const ITEMS_PER_PAGE = 10;

    /**
     * Carga vacaciones con paginación del lado del servidor
     */
    const loadVacaciones = async (pageNumber = 1) => {
        setLoading(true);

        try {
            const response = await fetch(
                `/api/vacaciones/ObtenerVacacionesSolicitadas?idUsuario=${perfil.idUsuario}&pageNumber=${pageNumber}&pageSize=${ITEMS_PER_PAGE}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const data = await response.json();

            if (data.iResponseType === 1 && data.detalles) {
                const detalles = JSON.parse(data.detalles);
                const vacacionesPaginadas = detalles.table2 || [];
                const paginationInfo = detalles.table3 && detalles.table3.length > 0
                    ? detalles.table3[0]
                    : { totalPages: 0, totalRecords: 0, currentPage: 1 };

                setVacacionesSolicitadas(vacacionesPaginadas);
                setTotalPages(paginationInfo.totalPages || 0);
                setTotalRecords(paginationInfo.totalRecords || 0);
                setCurrentPage(paginationInfo.currentPage || pageNumber);
            } else {
                setVacacionesSolicitadas([]);
                setTotalPages(0);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error al cargar vacaciones:', error);
            showErrorToast('Error de conexión al cargar las solicitudes de vacaciones');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Cargar datos iniciales
     */
    useEffect(() => {
        if (perfil && perfil.idUsuario) {
            setCurrentPage(1);
            loadVacaciones(1);
        }
    }, [perfil, refreshTrigger]);

    /**
     * Cambiar de página
     */
    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages && pageNumber !== currentPage) {
            loadVacaciones(pageNumber);
        }
    };

    const getStatusBadge = (estado) => {
        const estadoUpper = estado?.toUpperCase();
        const estados = {
            'PENDIENTE': { bg: 'warning', text: 'Pendiente' },
            'APROBADA': { bg: 'success', text: 'Aprobada' },
            'RECHAZADA': { bg: 'danger', text: 'Rechazada' },
            'CANCELADA': { bg: 'secondary', text: 'Cancelada' }
        };

        const status = estados[estadoUpper] || { bg: 'secondary', text: estado };
        return <Badge bg={status.bg}>{status.text}</Badge>;
    };

    const handleRefresh = () => {
        loadVacaciones(currentPage);
    };

    const handleRowClick = (vacacion) => {
        const fotoUrl = perfil.vFoto ? perfil.vFoto : '/img/team/nofoto50.png';

        const vacationData = {
            idVacacion: vacacion.idSolicitudVacaciones || vacacion.idVacacion,
            idUsuario: perfil.idUsuario, // Agregar el idUsuario del perfil
            name: perfil.vNombre + ' ' + perfil.vApellidos || 'Usuario',
            position: perfil.vNombreTipoUsuario || 'No especificado',
            photo: fotoUrl,
            department: perfil.vDepartamento || 'No especificado',
            categoria: perfil.vCategoria || 'No especificado',
            vacationStart: vacacion.dFechaInicio,
            vacationEnd: vacacion.dFechaFin,
            daysRequested: vacacion.iDiasSolicitados || 0,
            estado: vacacion.vEstado,
            comentarios: vacacion.vComentarios || '',
            vFechasJson: vacacion.vFechasJson || null // Agregar las fechas específicas
        };

        if (onVacationClick) {
            onVacationClick(vacationData);
        }
    };

    const generateVacationLetterPDF = (vacacion) => {
        try {
            // Parsear fechas específicas si existen
            let fechasEspecificas = null;
            if (vacacion.vFechasJson && vacacion.vFechasJson.trim() !== '') {
                try {
                    const fechasArray = JSON.parse(vacacion.vFechasJson);
                    fechasEspecificas = fechasArray.map(fechaStr => ({
                        dateString: fechaStr,
                        formatted: new Date(fechaStr + 'T00:00:00').toLocaleDateString('es-ES', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                        }),
                        isWeekend: [0, 6].includes(new Date(fechaStr + 'T00:00:00').getDay())
                    }));
                } catch (e) {
                    console.warn('Error al parsear vFechasJson en PDF:', e);
                }
            }

            const vacationData = {
                employeeName: `${perfil.vNombre} ${perfil.vApellidos}`,
                startDate: vacacion.dFechaInicio,
                endDate: vacacion.dFechaFin,
                totalDays: vacacion.iDiasSolicitados || 1,
                specificDates: fechasEspecificas
            };

            generatePDF(vacationData);

            showSuccessToast('Carta de solicitud de vacaciones generada exitosamente');

        } catch (error) {
            console.error('Error al generar el PDF:', error);
            alert('Error al generar el documento PDF. Por favor intente nuevamente.');
        }
    };

    /**
     * Renderiza los items de paginación
     */
    const renderPaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // Botón primera página
        if (startPage > 1) {
            items.push(
                <Pagination.First
                    key="first"
                    onClick={() => handlePageChange(1)}
                    disabled={loading}
                />
            );
        }

        // Botón página anterior
        items.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
            />
        );

        // Páginas numeradas
        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                    disabled={loading}
                >
                    {page}
                </Pagination.Item>
            );
        }

        // Botón página siguiente
        items.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
            />
        );

        // Botón última página
        if (endPage < totalPages) {
            items.push(
                <Pagination.Last
                    key="last"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={loading}
                />
            );
        }

        return items;
    };

    return (
        <div className="border rounded-3 p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                    <FontAwesomeIcon icon="calendar-check" className="me-2" />
                    Mis Solicitudes de Vacaciones
                    {totalRecords > 0 && (
                        <Badge bg="primary" pill className="ms-2">
                            {totalRecords}
                        </Badge>
                    )}
                </h6>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={loading}
                    title="Actualizar"
                >
                    <FontAwesomeIcon icon="sync-alt" className={loading ? 'fa-spin' : ''} />
                </Button>
            </div>

            {loading && vacacionesSolicitadas.length === 0 ? (
                <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Cargando solicitudes...</p>
                </div>
            ) : vacacionesSolicitadas.length === 0 ? (
                <div className="text-center py-4 text-muted">
                    <FontAwesomeIcon icon="inbox" size="3x" className="mb-3 opacity-50" />
                    <p>No hay solicitudes de vacaciones registradas</p>
                </div>
            ) : (
                <>
                    <div className="table-responsive">
                        <Table hover className="mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0">Fecha Inicio</th>
                                    <th className="border-0">Fecha Fin</th>
                                    <th className="border-0 text-center">D&iacute;as</th>
                                    <th className="border-0">Estado</th>
                                    <th className="border-0">Fecha Solicitud</th>
                                    <th className="border-0 text-center">Acci&oacute;n</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vacacionesSolicitadas.map((vacacion, index) => (
                                    <tr
                                        key={`${vacacion.idVacacion}-${index}`}
                                        onClick={() => handleRowClick(vacacion)}
                                        style={{ cursor: 'pointer' }}
                                        className="align-middle"
                                    >
                                        <td>{formatDate(vacacion.dFechaInicio)}</td>
                                        <td>{formatDate(vacacion.dFechaFin)}</td>
                                        <td className="text-center">
                                            <Badge bg="info" pill>{vacacion.iDiasSolicitados || 0}</Badge>
                                        </td>
                                        <td>{getStatusBadge(vacacion.vEstado)}</td>
                                        <td className="text-muted small">{formatDate(vacacion.dCreatedDate)}</td>
                                        <td className="text-center">
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                title="Generar Carta PDF"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    generateVacationLetterPDF(vacacion);
                                                }}
                                                disabled={isGenerating}
                                            >
                                                {isGenerating ? (
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                ) : (
                                                    <FontAwesomeIcon icon="file-pdf" />
                                                )}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    {/* Paginación */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                            <small className="text-muted">
                                Mostrando p&aacute;gina {currentPage} de {totalPages} ({totalRecords} total)
                            </small>
                            <Pagination className="mb-0" size="sm">
                                {renderPaginationItems()}
                            </Pagination>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

VacationRequestsTable.propTypes = {
    perfil: PropTypes.object.isRequired,
    setShowMensajeError: PropTypes.func.isRequired,
    setMensajeError: PropTypes.func.isRequired,
    setShowMensajeInformativo: PropTypes.func,
    setMensajeInformativo: PropTypes.func,
    onVacationClick: PropTypes.func,
    refreshTrigger: PropTypes.number,
    formatDate: PropTypes.func.isRequired
};

export default VacationRequestsTable;